import { useState, useRef } from 'react';
import api from '../lib/api';
import { useLookup } from '../contexts/LookupContext';
import { Printer, FileText, Phone as PhoneIcon, AddressBook, ClockCounterClockwise, ChartBar } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

const REPORT_TYPES = [
  { id: 'contacts', label: 'Contact Report', desc: 'Full details for each contact', icon: FileText },
  { id: 'phone-list', label: 'Phone List', desc: 'Name, company, and phone numbers', icon: PhoneIcon },
  { id: 'directory', label: 'Contact Directory', desc: 'Names, addresses, email', icon: AddressBook },
  { id: 'activity-history', label: 'Activity History', desc: 'All logged activities by date', icon: ClockCounterClockwise },
  { id: 'history-summary', label: 'History Summary', desc: 'Activity counts per contact', icon: ChartBar },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ReportsPage() {
  const { activeLookup, clearActiveLookup } = useLookup();
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const printRef = useRef(null);

  const runReport = async () => {
    if (!selectedReport) return;
    setLoading(true);
    setReportData(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (tagFilter) params.tag = tagFilter;
      // ACT! behavior: reports honor the active cross-page lookup.
      if (activeLookup?.query) params.lookup = JSON.stringify(activeLookup.query);
      else if (activeLookup?.ids?.length) params.lookup = JSON.stringify({ ids: activeLookup.ids });
      const { data } = await api.get(`/reports/${selectedReport}`, { params });
      setReportData({ type: selectedReport, data });
    } catch (err) {
      console.error('Report failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>SalesCrunchCRM Report</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; color: #333; margin: 20px; }
        h1 { font-size: 16px; color: #0B5394; border-bottom: 2px solid #0B5394; padding-bottom: 4px; }
        h2 { font-size: 13px; color: #CC6600; margin-top: 16px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { background: #E0E0E0; border: 1px solid #999; padding: 4px 6px; text-align: left; font-size: 10px; }
        td { border: 1px solid #CCC; padding: 3px 6px; font-size: 10px; }
        tr:nth-child(even) { background: #F9F9F9; }
        .contact-card { border: 1px solid #999; padding: 10px; margin-bottom: 12px; page-break-inside: avoid; }
        .contact-name { font-size: 13px; font-weight: bold; color: #0B5394; }
        .field-row { display: flex; gap: 4px; margin: 2px 0; }
        .field-label { font-weight: bold; min-width: 80px; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      ${content.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="flex flex-col h-full" data-testid="reports-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <FileText size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Reports</span>
        <WatchHowLink slug="reports" className="ml-1" />
        <div className="flex-1" />
        {reportData && (
          <button onClick={handlePrint} className="act-toolbar-btn" data-testid="print-report-btn">
            <Printer size={14} weight="bold" />
            <span className="text-[10px]">Print</span>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 h-full">
          {/* Report Selector */}
          <div className="bg-[#F5F5F0] border border-[#999]">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Report Type</h3>
            </div>
            <div className="divide-y divide-[#DDD]">
              {REPORT_TYPES.map(r => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedReport(r.id)}
                    className={`w-full text-left px-3 py-2.5 transition-colors ${selectedReport === r.id ? 'bg-[#0B5394] text-white' : 'hover:bg-[#E8E8E8]'}`}
                    data-testid={`report-type-${r.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={14} weight="bold" />
                      <div>
                        <p className={`text-[12px] font-medium ${selectedReport === r.id ? 'text-white' : 'text-[#333]'}`}>{r.label}</p>
                        <p className={`text-[10px] ${selectedReport === r.id ? 'text-white/70' : 'text-[#999]'}`}>{r.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Filters */}
            <div className="px-3 py-3 border-t border-[#999]">
              <h3 className="text-[11px] font-bold text-[#CC6600] uppercase tracking-wide mb-2">Filters</h3>
              {activeLookup && (
                <div className="mb-2 px-2 py-1.5 bg-[#FFF8E7] border border-[#F39C12]/60 text-[10px]" data-testid="report-lookup-banner">
                  <div className="text-[#7A4F00]">
                    <strong>Lookup active:</strong> {activeLookup.label}
                    {typeof activeLookup.count === 'number' && <> ({activeLookup.count})</>}
                    {' '}— report includes only these contacts.
                  </div>
                  <button
                    onClick={clearActiveLookup}
                    className="font-bold text-[#0B5394] hover:text-[#002FA7] underline underline-offset-2"
                    data-testid="report-lookup-clear-btn"
                  >
                    Use all contacts instead
                  </button>
                </div>
              )}
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] font-medium text-[#666]">Status</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full h-[26px] px-1.5 text-[11px] border border-[#B0B0B0] bg-white mt-0.5" data-testid="report-status-filter">
                    <option value="">All</option>
                    <option value="lead">Lead</option>
                    <option value="customer">Customer</option>
                    <option value="prospect">Prospect</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#666]">Tag</label>
                  <input value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="e.g. vip" autoComplete="one-time-code" className="w-full h-[26px] px-1.5 text-[11px] border border-[#B0B0B0] bg-white mt-0.5" data-testid="report-tag-filter" />
                </div>
              </div>
              <button
                onClick={runReport}
                disabled={!selectedReport || loading}
                className="w-full mt-3 px-3 py-1.5 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50"
                data-testid="run-report-btn"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {/* Report Output */}
          <div className="lg:col-span-3 bg-white border border-[#999] overflow-auto" data-testid="report-output">
            <div ref={printRef}>
              {!reportData ? (
                <p className="text-[12px] text-[#999] text-center py-16">Select a report type and click Generate</p>
              ) : reportData.type === 'contacts' ? (
                <div className="p-4">
                  <h1 style={{fontSize:'16px',color:'#0B5394',borderBottom:'2px solid #0B5394',paddingBottom:'4px'}}>Contact Report</h1>
                  <p style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{reportData.data.length} contacts | Generated {new Date().toLocaleDateString()}</p>
                  {reportData.data.map(c => (
                    <div key={c.contact_id} style={{border:'1px solid #CCC',padding:'10px',marginTop:'12px',pageBreakInside:'avoid'}}>
                      <div style={{fontSize:'13px',fontWeight:'bold',color:'#0B5394'}}>{c.first_name} {c.last_name}</div>
                      <div style={{fontSize:'11px',color:'#666',marginBottom:'6px'}}>{c.company} | {c.status}</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'2px',fontSize:'10px'}}>
                        <div><b>Phone:</b> {c.phone || '—'}</div>
                        <div><b>Email:</b> {c.email || '—'}</div>
                        <div><b>Address:</b> {[c.address_street, c.address_city, c.address_state, c.address_zip].filter(Boolean).join(', ') || '—'}</div>
                        <div><b>Activities:</b> {c.activity_count || 0}</div>
                        {c.tags?.length > 0 && <div><b>Tags:</b> {c.tags.join(', ')}</div>}
                        {c.notes && <div style={{gridColumn:'span 2'}}><b>Notes:</b> {c.notes}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : reportData.type === 'phone-list' ? (
                <div className="p-4">
                  <h1 style={{fontSize:'16px',color:'#0B5394',borderBottom:'2px solid #0B5394',paddingBottom:'4px'}}>Phone List</h1>
                  <p style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{reportData.data.length} contacts | Generated {new Date().toLocaleDateString()}</p>
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:'8px'}}>
                    <thead><tr style={{background:'#E0E0E0'}}>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Last Name</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>First Name</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Company</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Phone</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Mobile</th>
                    </tr></thead>
                    <tbody>
                      {reportData.data.map(c => (
                        <tr key={c.contact_id} style={{borderBottom:'1px solid #DDD'}}>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.last_name}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.first_name}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.company || '—'}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.phone}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.custom_fields?.mobile || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : reportData.type === 'directory' ? (
                <div className="p-4">
                  <h1 style={{fontSize:'16px',color:'#0B5394',borderBottom:'2px solid #0B5394',paddingBottom:'4px'}}>Contact Directory</h1>
                  <p style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{reportData.data.length} contacts | Generated {new Date().toLocaleDateString()}</p>
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:'8px'}}>
                    <thead><tr style={{background:'#E0E0E0'}}>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Name</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Company</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Address</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Phone</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Email</th>
                    </tr></thead>
                    <tbody>
                      {reportData.data.map(c => (
                        <tr key={c.contact_id}>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.first_name} {c.last_name}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.company || '—'}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{[c.address_street, c.address_city, c.address_state, c.address_zip].filter(Boolean).join(', ') || '—'}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.phone || '—'}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : reportData.type === 'activity-history' ? (
                <div className="p-4">
                  <h1 style={{fontSize:'16px',color:'#0B5394',borderBottom:'2px solid #0B5394',paddingBottom:'4px'}}>Activity History</h1>
                  <p style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{reportData.data.length} activities | Generated {new Date().toLocaleDateString()}</p>
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:'8px'}}>
                    <thead><tr style={{background:'#E0E0E0'}}>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Date</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Type</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Contact</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Regarding</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Details</th>
                    </tr></thead>
                    <tbody>
                      {reportData.data.map(a => (
                        <tr key={a.activity_id}>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{formatDate(a.created_at)}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textTransform:'capitalize'}}>{a.activity_type}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',color:'#0B5394'}}>{a.contact_name}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',fontWeight:'500'}}>{a.title}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{a.content || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.data.length === 0 && <p style={{textAlign:'center',color:'#999',padding:'20px',fontSize:'12px'}}>No activities found</p>}
                </div>
              ) : reportData.type === 'history-summary' ? (
                <div className="p-4">
                  <h1 style={{fontSize:'16px',color:'#0B5394',borderBottom:'2px solid #0B5394',paddingBottom:'4px'}}>History Summary</h1>
                  <p style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{reportData.data.length} contacts with activities | Generated {new Date().toLocaleDateString()}</p>
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:'8px'}}>
                    <thead><tr style={{background:'#E0E0E0'}}>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Contact</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'left',fontSize:'10px'}}>Company</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'center',fontSize:'10px'}}>Calls</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'center',fontSize:'10px'}}>Emails</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'center',fontSize:'10px'}}>Meetings</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'center',fontSize:'10px'}}>Notes</th>
                      <th style={{border:'1px solid #999',padding:'4px 6px',textAlign:'center',fontSize:'10px',fontWeight:'bold'}}>Total</th>
                    </tr></thead>
                    <tbody>
                      {reportData.data.map(c => (
                        <tr key={c.contact_id}>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',color:'#0B5394',fontWeight:'500'}}>{c.name}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px'}}>{c.company || '—'}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textAlign:'center'}}>{c.calls}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textAlign:'center'}}>{c.emails}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textAlign:'center'}}>{c.meetings}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textAlign:'center'}}>{c.notes}</td>
                          <td style={{border:'1px solid #DDD',padding:'3px 6px',fontSize:'10px',textAlign:'center',fontWeight:'bold'}}>{c.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.data.length === 0 && <p style={{textAlign:'center',color:'#999',padding:'20px',fontSize:'12px'}}>No activity history found</p>}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
